// ============================================
// NRS.04 — Safety Output Filter Service
// ============================================
// Formalized from: src/services/safety/nurseOutputFilter.ts + driftMonitor.ts
// Spec: hlx-nrs-04-safety-output-filter.md (LOCKED)
// Contract: 22 pattern rules (5 categories), 5 drift dimensions,
//           bypass detection, max 2 regen, safe fallback
// ============================================

import type {
  FilterRule,
  FilterResult,
  FilterViolation,
  DriftDimension,
  DriftAction,
  DriftState,
  DriftTurn,
  DriftFlag,
  SafetyOutputFilterMetrics,
} from '../types/safetyOutputFilter';

// ── Filter Rules: 22 patterns across 5 categories ──

const FILTER_RULES: FilterRule[] = [
  // Clinical Boundary (CB) — 7 rules, severity: block
  { ruleId: 'CB-01', category: 'clinical_boundary', severity: 'block', pattern: /\byou have\b.*\b(?:disease|disorder|syndrome|condition|diagnosis)\b/i, message: 'Diagnostic language: "you have [disease]"' },
  { ruleId: 'CB-02', category: 'clinical_boundary', severity: 'block', pattern: /\bdiagnos(?:e|ed|is|ing)\b/i, message: 'Diagnostic terminology used' },
  { ruleId: 'CB-03', category: 'clinical_boundary', severity: 'block', pattern: /\byou(?:'re| are) (?:suffering from|afflicted with)\b/i, message: 'Disease attribution language' },
  { ruleId: 'CB-04', category: 'clinical_boundary', severity: 'block', pattern: /\bthis (?:means|indicates|confirms) you have\b/i, message: 'Diagnosis confirmation from labs' },
  { ruleId: 'CB-05', category: 'clinical_boundary', severity: 'block', pattern: /\bI(?:'m| am) prescribing\b/i, message: 'Prescribing claim' },
  { ruleId: 'CB-06', category: 'clinical_boundary', severity: 'block', pattern: /\btake (?:this|these) (?:medication|drug|prescription)\b/i, message: 'Medication direction' },
  { ruleId: 'CB-07', category: 'clinical_boundary', severity: 'block', pattern: /\byou(?:'ll| will) need (?:medication|drugs|a prescription)\b/i, message: 'Medication need assertion' },
  { ruleId: 'CB-08', category: 'clinical_boundary', severity: 'block', pattern: /\byou have\b\s+(?:a\s+)?(?:\w+\s+){0,3}(?:hypothyroidism|hyperthyroidism|anemia|diabetes|hypertension|cancer|cirrhosis|hepatitis|arthritis|osteoporosis|fibromyalgia|lupus|PCOS|endometriosis|neuropathy|cardiomyopathy|arrhythmia)\b/i, message: 'Diagnostic language: "you have [specific disease]"' },
  { ruleId: 'CB-09', category: 'clinical_boundary', severity: 'block', pattern: /Dr\.?\s+(?:Mean\s+Platelet|Ferritin|Hemoglobin|TSH|LDL|HDL|BUN|RDW|MCV|MPV|WBC|Cholesterol|Estradiol|Testosterone|Progesterone|Vitamin\s+D|Potassium|Glucose|Creatinine)/i, message: 'Biomarker name used as doctor name' },

  // Safety / Fabrication (SF) — 4 rules, severity: block
  { ruleId: 'SF-01', category: 'safety', severity: 'block', pattern: /\bstudies (?:show|prove|confirm|demonstrate) that\b.*\b(?:cures?|eliminates?|reverses?)\b/i, message: 'Unsubstantiated cure claim' },
  { ruleId: 'SF-02', category: 'safety', severity: 'block', pattern: /\bguaranteed? to\b/i, message: 'Guarantee language' },
  { ruleId: 'SF-03', category: 'safety', severity: 'block', pattern: /\b100%\s*(?:effective|safe|cure)\b/i, message: 'Absolute efficacy claim' },
  { ruleId: 'SF-04', category: 'safety', severity: 'block', pattern: /\bclinically proven to cure\b/i, message: 'Clinically proven to cure' },

  // Professional Distance — Over-familiar (PD-0x) — 4 rules, severity: warn
  { ruleId: 'PD-01', category: 'professional_distance', severity: 'warn', pattern: /\b(?:sweetie|honey|darling|babe|hun|sweetheart|love)\b/i, message: 'Pet name / terms of endearment' },
  { ruleId: 'PD-02', category: 'professional_distance', severity: 'warn', pattern: /\bI(?:'m| am) (?:Sarah|Maya|Jessica|Emily|Anna|Rachel|Lauren|Megan|Nicole|Ashley)\b/i, message: 'Personal name introduction' },
  { ruleId: 'PD-03', category: 'professional_distance', severity: 'warn', pattern: /\blol\b|\bromfl?\b|\bhaha\b/i, message: 'Internet slang' },
  { ruleId: 'PD-04', category: 'professional_distance', severity: 'warn', pattern: /(?:😂|🤣|😜|😘|❤️|💕|🥰)/u, message: 'Inappropriate emoji' },

  // Professional Distance — Dismissive (PD-1x) — 3 rules, severity: block
  { ruleId: 'PD-10', category: 'professional_distance', severity: 'block', pattern: /\bthat(?:'s| is) not (?:my|our) (?:problem|concern|issue)\b/i, message: 'Dismissive of patient concern' },
  { ruleId: 'PD-11', category: 'professional_distance', severity: 'block', pattern: /\byou(?:'re| are) (?:wrong|mistaken|confused)\b/i, message: 'Told patient wrong/confused' },
  { ruleId: 'PD-12', category: 'professional_distance', severity: 'block', pattern: /\bjust deal with it\b/i, message: 'Dismissive language' },

  // Formatting (FMT) — 4 rules, severity: info
  { ruleId: 'FMT-01', category: 'formatting', severity: 'info', pattern: /^\s*[-*]\s/m, message: 'Markdown bullet points' },
  { ruleId: 'FMT-02', category: 'formatting', severity: 'info', pattern: /^\s*\d+\.\s/m, message: 'Numbered list formatting' },
  { ruleId: 'FMT-03', category: 'formatting', severity: 'info', pattern: /\*\*[^*]+\*\*/m, message: 'Bold markdown formatting' },
  { ruleId: 'FMT-04', category: 'formatting', severity: 'info', pattern: /^#{1,6}\s/m, message: 'Markdown headers' },
];

// ── Drift Monitoring ──

const DRIFT_THRESHOLDS: Record<DriftDimension, number> = {
  empathy: 0.3,
  formality: 0.4,
  verbosity: 0.5,
  clinical_language: 0.2,
  tone_consistency: 0.3,
};

export function createDriftState(sessionId: string, agentRole: string): DriftState {
  return {
    sessionId,
    agentRole,
    turns: [],
    cumulativeDrift: {
      empathy: 0,
      formality: 0,
      verbosity: 0,
      clinical_language: 0,
      tone_consistency: 0,
    },
    flags: [],
  };
}

function analyzeDrift(text: string, _turnNumber: number): Record<DriftDimension, number> {
  const words = text.split(/\s+/).length;
  const sentences = text.split(/[.!?]+/).filter(Boolean).length;

  // Simple heuristic scores (0-1)
  const empathySignals = (text.match(/\b(?:understand|hear you|feel|sorry|tough|hard)\b/gi) || []).length;
  const clinicalTerms = (text.match(/\b(?:biomarker|suboptimal|deficient|elevated|pathology|etiology)\b/gi) || []).length;
  const formalTerms = (text.match(/\b(?:furthermore|therefore|additionally|consequently|regarding)\b/gi) || []).length;

  return {
    empathy: Math.min(1, empathySignals / Math.max(sentences, 1)),
    formality: Math.min(1, formalTerms / Math.max(sentences, 1)),
    verbosity: Math.min(1, words / 200), // >200 words = max verbosity
    clinical_language: Math.min(1, clinicalTerms / Math.max(sentences, 1)),
    tone_consistency: 0, // Computed across turns
  };
}

function recordDriftTurn(state: DriftState, text: string): { state: DriftState; action: DriftAction; flags: DriftFlag[] } {
  const turnNumber = state.turns.length + 1;
  const scores = analyzeDrift(text, turnNumber);
  const flags: DriftFlag[] = [];

  // Check each dimension against threshold
  for (const dim of Object.keys(scores) as DriftDimension[]) {
    if (dim === 'tone_consistency') continue; // Handled separately
    const score = scores[dim];
    if (score > DRIFT_THRESHOLDS[dim]) {
      flags.push({
        type: `${dim}_drift`,
        severity: score > DRIFT_THRESHOLDS[dim] * 2 ? 'critical' : 'warn',
        message: `${dim} drift detected: ${score.toFixed(2)} > threshold ${DRIFT_THRESHOLDS[dim]}`,
      });
    }
  }

  // Tone consistency: compare to previous turns
  if (state.turns.length > 0) {
    const prevScores = state.turns[state.turns.length - 1].scores;
    let totalShift = 0;
    for (const dim of Object.keys(scores) as DriftDimension[]) {
      if (dim === 'tone_consistency') continue;
      totalShift += Math.abs(scores[dim] - (prevScores[dim] || 0));
    }
    scores.tone_consistency = totalShift / 4;
    if (scores.tone_consistency > DRIFT_THRESHOLDS.tone_consistency) {
      flags.push({
        type: 'tone_consistency_drift',
        severity: 'warn',
        message: `Tone consistency drift: ${scores.tone_consistency.toFixed(2)}`,
      });
    }
  }

  const turn: DriftTurn = { turnNumber, scores, flags, timestamp: new Date() };
  const newState: DriftState = {
    ...state,
    turns: [...state.turns, turn],
    flags: [...state.flags, ...flags],
    cumulativeDrift: {
      empathy: state.cumulativeDrift.empathy + scores.empathy,
      formality: state.cumulativeDrift.formality + scores.formality,
      verbosity: state.cumulativeDrift.verbosity + scores.verbosity,
      clinical_language: state.cumulativeDrift.clinical_language + scores.clinical_language,
      tone_consistency: state.cumulativeDrift.tone_consistency + scores.tone_consistency,
    },
  };

  const hasCritical = flags.some((f) => f.severity === 'critical');
  const action: DriftAction = hasCritical ? 'regenerate' : flags.length > 0 ? 'flag_for_review' : 'pass';

  return { state: newState, action, flags };
}

// ── Filter Engine ──

function runRules(text: string): FilterViolation[] {
  const violations: FilterViolation[] = [];
  for (const rule of FILTER_RULES) {
    const match = text.match(rule.pattern);
    if (match) {
      violations.push({
        ruleId: rule.ruleId,
        category: rule.category,
        severity: rule.severity,
        message: rule.message,
        matchedText: match[0],
      });
    }
  }
  return violations;
}

/**
 * Run the full safety output filter on a nurse response.
 * Three-defense system: rules + drift + bypass detection.
 */
export function filterNurseOutput(
  responseText: string,
  driftState: DriftState,
): FilterResult {
  // Defense 1: Pattern rules (22 rules, 5 categories)
  const violations = runRules(responseText);

  // Defense 2: Drift monitoring (5 dimensions)
  const driftResult = recordDriftTurn(driftState, responseText);

  // Map drift flags to violations
  for (const flag of driftResult.flags) {
    violations.push({
      ruleId: `DRIFT-${flag.type.toUpperCase()}`,
      category: 'professional_distance',
      severity: flag.severity === 'critical' ? 'block' : 'warn',
      message: flag.message,
    });
  }

  // Determine pass/fail
  const hasBlocks = violations.some((v) => v.severity === 'block');
  const passed = !hasBlocks;

  // Determine action
  let driftAction = driftResult.action;
  if (hasBlocks && driftAction === 'pass') {
    driftAction = 'regenerate';
  }

  return {
    text: responseText,
    passed,
    driftAction,
    violations,
    driftState: driftResult.state,
  };
}

/**
 * Quick check for clinical boundary violations only.
 */
export function checkClinicalBoundaries(text: string): FilterViolation[] {
  return FILTER_RULES
    .filter((r) => r.category === 'clinical_boundary' || r.category === 'safety')
    .reduce<FilterViolation[]>((acc, rule) => {
      const match = text.match(rule.pattern);
      if (match) {
        acc.push({ ruleId: rule.ruleId, category: rule.category, severity: rule.severity, message: rule.message, matchedText: match[0] });
      }
      return acc;
    }, []);
}

/**
 * Quick check for professional distance only.
 */
export function checkProfessionalDistance(text: string): FilterViolation[] {
  return FILTER_RULES
    .filter((r) => r.category === 'professional_distance')
    .reduce<FilterViolation[]>((acc, rule) => {
      const match = text.match(rule.pattern);
      if (match) {
        acc.push({ ruleId: rule.ruleId, category: rule.category, severity: rule.severity, message: rule.message, matchedText: match[0] });
      }
      return acc;
    }, []);
}

/**
 * Get all defined filter rules (for testing/auditing).
 */
export function getFilterRules(): ReadonlyArray<Omit<FilterRule, 'pattern'> & { patternSource: string }> {
  return FILTER_RULES.map((r) => ({
    ruleId: r.ruleId,
    category: r.category,
    severity: r.severity,
    message: r.message,
    patternSource: r.pattern.source,
  }));
}

// ── Metrics ──

let _metrics: SafetyOutputFilterMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};

export function getMetrics(): SafetyOutputFilterMetrics {
  return { ..._metrics };
}
